import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import { VisibilityOffOutlined, VisibilityOutlined } from "@material-ui/icons"
import { Form, Formik } from "formik"
import PropTypes from "prop-types"
import { useContext, useState } from "react"
import * as Yup from "yup"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import AppContext from "~/contexts/App"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"

import Box from "~/components/Common/Box"
import cleanPermissionName from "~/utils/cleanPermissionName"
import useStyles from "./styles"

const MyAccountDialog = () => {
  const classes = useStyles()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down("sm"))
  const openErrorSnackbar = useOpenErrorSnackbar()

  const {
    dialogs,
    closeDialog,
    authUser,
    updatePasswordMutation,
    openSnackBar,
  } = useContext(AppContext)
  const thisDialog = dialogs?.["myAccountDialog"] || {}
  const { open = false, data } = thisDialog

  const [visibility, setVisibility] = useState(true)

  const handleClose = () => {
    closeDialog("myAccountDialog", false)
    setVisibility(true)
  }

  const schema = Yup.object().shape({
    current_password: Yup.string()
      .matches(
        /^(?=.*[\d])(?=.*[!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/])(?=.*[A-Z])[A-Za-z\d!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/]{8,}$/,
        "Must contain at least 8 characters, one capital letter, one number and one special character",
      )
      .required("Current Password is required"),
    new_password: Yup.string()
      .matches(
        /^(?=.*[\d])(?=.*[!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/])(?=.*[A-Z])[A-Za-z\d!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/]{8,}$/,
        "Must contain at least 8 characters, one capital letter, one number and one special character",
      )
      .required("New Password is required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("new_password"), null], "Passwords do not match")
      .required("Confirm Password is required"),
  })

  const onSubmit = (values, actions) => {
    const data = {
      current_password: values.current_password,
      new_password: values.new_password,
    }
    updatePasswordMutation
      .mutateAsync(data)
      .then((res) => {
        actions.setSubmitting(false)
        setVisibility(true)
        openSnackBar({
          message: "Password Reset Successfully",
        })
        handleClose()
      })
      .catch((err) => {
        actions.setSubmitting(false)
        openErrorSnackbar(err, "Error Resetting Password")
      })
  }

  return (
    <Dialog maxWidth="xs" open={open} fullScreen={smDown}>
      <DialogTitle disableTypography>
        <Typography color="primary" variant="h2" style={{ fontWeight: 600 }}>
          My Account
        </Typography>
        <Typography variant="subtitle1" color="primary">
          Edit Your Account Information
        </Typography>
      </DialogTitle>
      <Formik
        initialValues={{
          current_password: "",
          new_password: "",
          confirm_password: "",
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({
          handleChange,
          values,
          errors,
          touched,
          isSubmitting,
          handleSubmit,
        }) => (
          <Form onSubmit={handleSubmit}>
            <DialogContent>
              <Box marginBottom="16px">
                <Typography color="secondary" className={classes.title}>
                  Full Name
                </Typography>
                <TextField
                  disabled
                  type="text"
                  value={authUser ? authUser.name : ""}
                  InputProps={{
                    classes: {
                      root: classes.inputBackgroundColor,
                    },
                  }}
                  variant="filled"
                  size="small"
                  fullWidth
                />
              </Box>
              <Box marginBottom="16px">
                <Typography color="secondary" className={classes.title}>
                  Email
                </Typography>
                <TextField
                  disabled
                  type="text"
                  value={authUser ? authUser.email : ""}
                  InputProps={{
                    classes: {
                      root: classes.inputBackgroundColor,
                    },
                  }}
                  variant="filled"
                  size="small"
                  fullWidth
                  onChange={handleChange}
                  error={!!touched.email && !!errors.email}
                  helperText={
                    <span>
                      {touched.email && errors.email ? errors.email : " "}
                    </span>
                  }
                />
              </Box>
              <Box marginBottom="32px">
                <Typography color="secondary" className={classes.title}>
                  Permission Group
                </Typography>
                <TextField
                  disabled
                  type="text"
                  value={cleanPermissionName(authUser?.permission_group)}
                  InputProps={{
                    classes: {
                      root: classes.inputBackgroundColor,
                    },
                  }}
                  variant="filled"
                  size="small"
                  fullWidth
                />
              </Box>
              <Divider />
              <Box marginTop="32px">
                <Typography color="primary" className={classes.subtitle}>
                  Reset Password
                </Typography>
              </Box>
              <Box marginTop="16px">
                <Typography color="secondary" className={classes.title}>
                  Current Password
                </Typography>
                <TextField
                  type={visibility ? "password" : "text"}
                  value={values.current_password}
                  name="current_password"
                  error={touched.current_password && errors.current_password}
                  helperText={
                    touched.current_password && errors.current_password
                      ? errors.current_password
                      : " "
                  }
                  InputProps={{
                    classes: {
                      root: classes.inputBackgroundColor,
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setVisibility(!visibility)}
                        >
                          {visibility ? (
                            <VisibilityOffOutlined
                              color="primary"
                              fontSize="small"
                            />
                          ) : (
                            <VisibilityOutlined
                              color="primary"
                              fontSize="small"
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  variant="filled"
                  size="small"
                  fullWidth
                  onChange={handleChange}
                />
              </Box>
              <Box marginBottom="16px">
                <Typography color="secondary" className={classes.title}>
                  New Password
                </Typography>
                <TextField
                  type={visibility ? "password" : "text"}
                  name="new_password"
                  value={values.new_password}
                  InputProps={{
                    classes: {
                      root: classes.inputBackgroundColor,
                    },
                  }}
                  variant="filled"
                  size="small"
                  fullWidth
                  onChange={handleChange}
                  error={touched.new_password && errors.new_password}
                  helperText={
                    <span>
                      {touched.new_password && errors.new_password
                        ? errors.new_password
                        : " "}
                    </span>
                  }
                />
              </Box>
              <Box>
                <Typography color="secondary" className={classes.title}>
                  Confirm Password
                </Typography>
                <TextField
                  type={visibility ? "password" : "text"}
                  value={values.confirm_password}
                  name="confirm_password"
                  InputProps={{
                    classes: {
                      root: classes.inputBackgroundColor,
                    },
                  }}
                  variant="filled"
                  size="small"
                  fullWidth
                  onChange={handleChange}
                  error={touched.confirm_password && errors.confirm_password}
                  helperText={
                    touched.confirm_password && errors.confirm_password
                      ? errors.confirm_password
                      : " "
                  }
                />
              </Box>
            </DialogContent>
            <DialogActions className={classes.actions}>
              <Box m={2}>
                <Button style={{ marginRight: "8px" }} onClick={handleClose}>
                  Cancel
                </Button>
                <LoaderButton
                  type="submit"
                  classoverrides={{
                    wrapper: classes.button,
                    button: {
                      root: classes.buttonRoot,
                    },
                  }}
                  variant="contained"
                  color="secondary"
                  working={isSubmitting}
                >
                  Save
                </LoaderButton>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

MyAccountDialog.propTypes = {
  children: PropTypes.array,
}

export default MyAccountDialog
